import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { useGetIdentity, useGetList, useNotify } from "ra-core";
import { MessageSquare, X } from "lucide-react";
import type { Ticket } from "@/modules/types";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { SignedMemberAvatarImage } from "@/components/avatar/SignedMemberAvatarImage";
import { cn } from "@/lib/utils";
import { ticketShowPath } from "@/modules/tickets/ticketStatusWorkflow";
import { useOpenDirectMessage } from "@/modules/messages/useDirectMessage";
import { VoiceCallButton } from "@/modules/voice/VoiceCallButton";
import {
  localTimeIn,
  workLocationLabel,
} from "@/modules/teamMap/teamMapCities";
import type { TeamMapMember } from "@/modules/teamMap/TeamMapScene";

type TaskRow = { id: number; text?: string | null; due_date?: string | null };
type MeetingRow = {
  id: number;
  title?: string | null;
  event_date?: string | null;
  event_time?: string | null;
};

const todayKey = () => new Date().toISOString().slice(0, 10);
const startOfWeekIso = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - ((date.getDay() + 6) % 7));
  return date.toISOString();
};

const Section = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <section className="space-y-1.5">
    <h4 className="text-2xs font-medium tracking-wide text-muted-foreground uppercase">
      {label}
    </h4>
    {children}
  </section>
);

const Stat = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="rounded-lg bg-muted/60 px-3 py-2">
    <div className="text-lg font-semibold tabular-nums">{value}</div>
    <div className="text-xs text-muted-foreground">{label}</div>
  </div>
);

/**
 * What a teammate is doing: status and local time, their open tickets,
 * today's tasks and next meetings, this week's results, and ways to reach
 * them.
 */
export const TeamMapMemberCard = ({
  entry,
  onClose,
}: {
  entry: TeamMapMember;
  onClose: () => void;
}) => {
  const { member, location, online } = entry;
  const memberId = member.id;
  const navigate = useNavigate();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const { openDirectMessage } = useOpenDirectMessage();
  const isMe = String(identity?.id) === String(memberId);
  const name =
    [member.first_name, member.last_name].filter(Boolean).join(" ") ||
    member.email;
  const mine = {
    "@or": {
      "organization_member_id@eq": memberId,
      "assignee_person_ids@cs": `{${memberId}}`,
    },
  };

  const { data: tickets = [], total: ticketTotal = 0 } = useGetList<Ticket>(
    "tickets",
    {
      filter: {
        "assignee_id@eq": memberId,
        "status@in": "(new,open,waiting)",
        "merged_into_ticket_id@is": null,
      },
      pagination: { page: 1, perPage: 3 },
      sort: { field: "updated_at", order: "DESC" },
    },
  );
  const { data: tasks = [], total: taskTotal = 0 } = useGetList<TaskRow>(
    "tasks",
    {
      filter: {
        ...mine,
        "done_date@is": null,
        "due_date@lte": `${todayKey()}T23:59:59`,
      },
      pagination: { page: 1, perPage: 3 },
      sort: { field: "due_date", order: "ASC" },
    },
  );
  const { data: meetings = [] } = useGetList<MeetingRow>("calendar_events", {
    filter: {
      "@or": {
        "organization_member_id@eq": memberId,
        "assignee_member_ids@cs": `{${memberId}}`,
      },
      "event_date@gte": todayKey(),
      "completed_at@is": null,
    },
    pagination: { page: 1, perPage: 3 },
    sort: { field: "event_date", order: "ASC" },
  });
  const { total: resolvedThisWeek = 0 } = useGetList<Ticket>("tickets", {
    filter: { "assignee_id@eq": memberId, "resolved_at@gte": startOfWeekIso() },
    pagination: { page: 1, perPage: 1 },
    sort: { field: "id", order: "DESC" },
  });
  const { total: tasksDoneThisWeek = 0 } = useGetList<TaskRow>("tasks", {
    filter: { ...mine, "done_date@gte": startOfWeekIso() },
    pagination: { page: 1, perPage: 1 },
    sort: { field: "id", order: "DESC" },
  });

  const message = async () => {
    try {
      const conversation = await openDirectMessage(member);
      navigate(`/messages?conversation=${conversation.id}`);
    } catch (error) {
      notify(error instanceof Error ? error.message : "Could not open chat", {
        type: "error",
      });
    }
  };

  return (
    <aside className="flex max-h-full w-80 flex-col overflow-hidden rounded-xl border bg-card shadow-overlay">
      <div className="flex items-start gap-3 border-b p-4">
        <Avatar className="size-11">
          <SignedMemberAvatarImage member={member} size={88} alt={name} />
          <AvatarFallback>{name.slice(0, 2).toUpperCase()}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <p className="truncate text-base font-semibold">{name}</p>
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span
              aria-hidden
              className={cn(
                "size-2 rounded-full",
                online ? "bg-success" : "bg-muted-foreground/40",
              )}
            />
            {online ? "Online" : "Offline"}
            {" · "}
            {localTimeIn(location.tz)} local
          </p>
          <p className="truncate text-xs text-muted-foreground">
            {workLocationLabel(location)}
          </p>
        </div>
        <IconButton aria-label="Close" onClick={onClose} className="size-7">
          <X className="size-4" />
        </IconButton>
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
        <Section label="This week">
          <div className="grid grid-cols-2 gap-2">
            <Stat label="Tickets resolved" value={resolvedThisWeek} />
            <Stat label="Tasks done" value={tasksDoneThisWeek} />
          </div>
        </Section>

        <Section label={`Open tickets ${ticketTotal}`}>
          {tickets.length ? (
            <ul>
              {tickets.map((ticket) => (
                <li key={ticket.id}>
                  <Link
                    to={ticketShowPath(ticket.id)}
                    className="-mx-2 block truncate rounded-md px-2 py-1 text-sm hover:bg-muted"
                  >
                    {ticket.subject || `Ticket #${ticket.id}`}
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-muted-foreground">No open tickets.</p>
          )}
        </Section>

        <Section label={`Due today ${taskTotal}`}>
          {tasks.length ? (
            <ul className="space-y-0.5 text-sm">
              {tasks.map((task) => (
                <li key={task.id} className="truncate">
                  {task.text || "Task"}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-muted-foreground">Nothing due.</p>
          )}
        </Section>

        <Section label="Next meetings">
          {meetings.length ? (
            <ul className="space-y-0.5 text-sm">
              {meetings.map((meeting) => (
                <li key={meeting.id} className="truncate">
                  {meeting.title || "Meeting"}
                  <span className="text-xs text-muted-foreground">
                    {" · "}
                    {meeting.event_date}
                    {meeting.event_time
                      ? ` ${meeting.event_time.slice(0, 5)}`
                      : ""}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-muted-foreground">None scheduled.</p>
          )}
        </Section>
      </div>

      {isMe ? null : (
        <div className="flex gap-2 border-t p-3">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="flex-1"
            onClick={() => void message()}
          >
            <MessageSquare className="size-4" />
            Message
          </Button>
          {member.notification_phone ? (
            <VoiceCallButton phoneNumber={member.notification_phone} />
          ) : null}
        </div>
      )}
    </aside>
  );
};
